import { Link } from "react-router-dom";
import { Bath, CalendarHeart, CarFront, Coffee, Footprints, HeartHandshake, Pill, Sparkles, UtensilsCrossed } from "lucide-react";
import { IconCard, Notice, PageHero, SectionHead } from "../components/site/Blocks";
import { CtaBand } from "../components/site/Cta";
import { CareQuestionnaire } from "../components/forms/CareQuestionnaire";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const SERVICES = [
  [Coffee, "Companionship", "Conversation, shared activities, outings and a friendly, consistent presence."],
  [Bath, "Personal support", "Respectful help with bathing, dressing, grooming and toileting."],
  [Sparkles, "Activities of daily living", "Assistance with everyday routines that support comfort and independence."],
  [UtensilsCrossed, "Meal preparation", "Planning and preparing meals that reflect preferences and dietary needs."],
  [Footprints, "Mobility assistance", "Support with walking, positioning and safe movement around the home."],
  [CalendarHeart, "Respite", "Planned breaks for family caregivers, from a few hours to longer periods."],
  [Pill, "Reminders and routines", "Medication reminders and help keeping daily routines on track."],
  [CarFront, "Errands and appointments", "Accompaniment to appointments, shopping and community activities."],
];
const FAQ = [
  ["Can I inquire on behalf of a family member?", "Yes. Many inquiries come from adult children, spouses, friends or professionals arranging support for someone else. The questionnaire lets you tell us who you are and who needs support."],
  ["Is care available immediately?", "Availability depends on location, schedule and the type of support needed. We review each inquiry individually and will be clear with you about what may be possible and when."],
  ["Do you provide medical or nursing care?", "Our in-home services focus on non-medical support such as companionship, personal support and help with daily activities. If medical care is needed, we can talk about how to connect with appropriate providers."],
  ["Will insurance or government funding cover the cost?", "Coverage depends on your plan or program. We cannot guarantee reimbursement or funding eligibility, but we are happy to discuss payment arrangements during your consultation."],
  ["Which areas do you serve?", "We serve the Greater Toronto Area in Ontario. Tell us where the person needing care lives and we will let you know how we can help."],
];

export default function InHomeCare() {
  return (
    <>
      <PageHero eyebrow="In-Home Care" title="Our caregivers come to you." image="/images/outdoor.jpg" alt="A caregiver and an older adult enjoying time outdoors" testId="inhome-hero"
        actions={<a href="#care-questionnaire" className="btn-pill btn-primary" data-testid="inhome-find-care-cta-button">Find Care</a>}>
        <p>In-home care is one of our three main services. Our caregivers visit clients in their own homes, providing non-medical support that helps adults remain safe, comfortable and connected where they feel most at home. We work with each person and their family to understand daily routines, preferences and goals.</p>
      </PageHero>

      <section className="container-site py-16 sm:py-24">
        <SectionHead eyebrow="Types of support" title="Support you can inquire about.">
          Every arrangement is individual. Choose the kinds of help that matter most and we will talk through the details with you.
        </SectionHead>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {SERVICES.map(([icon, t, d]) => <IconCard key={t} icon={icon} title={t} testId={`inhome-service-${t.toLowerCase().replace(/\s+/g, "-")}`}>{d}</IconCard>)}
        </div>
      </section>

      <section className="bg-[#F3EFEA] border-y border-[#E5E0D8]">
        <div className="container-site py-16 sm:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <SectionHead eyebrow="Who can inquire" title="For individuals and the people who care about them.">
            You can complete an inquiry for yourself, or on behalf of a parent, spouse, relative, friend or client. If you are arranging care for someone else, we encourage you to involve them in the conversation wherever possible.
          </SectionHead>
          <div className="space-y-4">
            {["Adults who want to remain living at home", "Family members coordinating care for a loved one", "Friends and neighbours helping someone they know", "Case managers and social workers arranging support for clients"].map((t) => (
              <div key={t} className="flex items-center gap-3 rounded-xl bg-white border border-[#E5E0D8] px-5 py-4">
                <HeartHandshake className="h-5 w-5 text-[#C86D51] shrink-0" aria-hidden="true" /><span className="text-sm font-medium">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="care-questionnaire" className="container-site py-16 sm:py-24 scroll-mt-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4 space-y-6">
          <SectionHead eyebrow="Find Care" title="Tell us about the support you need.">
            This guided questionnaire takes about five minutes. Questions adapt to your answers, and you can go back at any time.
          </SectionHead>
          <Notice testId="inhome-availability-notice">Submitting an inquiry does not guarantee staffing, immediate availability, insurance reimbursement or government funding. We will discuss what is possible with you directly.</Notice>
          <p className="text-sm text-[#5A6561]">Prefer to send a general question instead? <Link to="/contact" className="underline text-[#1E3A2B]" data-testid="inhome-contact-link">Contact us</Link>.</p>
        </div>
        <div className="lg:col-span-8"><CareQuestionnaire /></div>
      </section>

      <section className="container-site pb-8">
        <SectionHead eyebrow="Common questions" title="Frequently asked questions" />
        <Accordion type="single" collapsible className="mt-8 max-w-3xl" data-testid="inhome-faq">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`}>
              <AccordionTrigger className="text-left text-base font-semibold" data-testid={`inhome-faq-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-sm text-[#5A6561] leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
      <CtaBand prefix="inhome-band" title="Looking for housing, a career, or a partnership?" />
    </>
  );
}
